import { useMutation } from "@apollo/client";
import {
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  InternalAdminEditUserMutation,
  InternalAdminEditUserMutationVariables,
  InternalAdminUserFragment,
} from "gql/graphql";
import { useUsers } from "modules/providers/users-provider";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import colors from "app/colors";
import {
  CancelIcon,
  CheckIcon,
  CloseIcon,
  PencilOutlineIcon,
} from "modules/icons";
import { FieldValues, useForm } from "react-hook-form";
import { getMessageFromError } from "modules/utils/Functions";
import { isEmpty } from "lodash";
import useIsHovering from "modules/hooks/use-is-hovering";
import useGlobalEditing from "modules/hooks/use-global-editing";
import INTERNAL_ADMIN_EDIT_USER_MUTATION from "../_networking/internal-admin-edit-user";

interface IProps {
  user: InternalAdminUserFragment;
}

const InternalAdminEditUserEmail = ({ user }: IProps) => {
  const { users } = useUsers();
  const { isHovering, onMouseOut, onMouseOver } = useIsHovering();

  const { mode, setMode } = useGlobalEditing(
    `InternalAdminEditUserEmail-${user.id}`,
  );

  const [internalAdminEditUserMutationAPI, { loading }] = useMutation<
    InternalAdminEditUserMutation,
    InternalAdminEditUserMutationVariables
  >(INTERNAL_ADMIN_EDIT_USER_MUTATION);

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    clearErrors,
    setValue,
    watch,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      email: user?.email || "",
    },
  });

  const onClearClicked = useCallback(() => {
    setValue("email", "");
  }, [setValue]);

  const email = watch("email");

  const isDuplicateEmail = useMemo(
    () =>
      !isEmpty(email) &&
      users.filter((_user) => _user.id !== user.id && _user.email === email)
        .length > 0,
    [email, user.id, users],
  );

  useEffect(() => {
    if (isDuplicateEmail) {
      setError("email", {
        message: "This email address is already taken",
      });
    } else {
      clearErrors("email");
    }
  }, [clearErrors, isDuplicateEmail, setError]);

  const onSubmit = useCallback(
    async ({ email }: FieldValues) => {
      if (isEmpty(user.phone) && isEmpty(email)) {
        setError("email", {
          message: "A user must have either an email or a phone number",
        });
        return;
      }
      internalAdminEditUserMutationAPI({
        variables: {
          organizationId: user.organizationId,
          id: user.id,
          input: {
            email,
          },
        },
        onError: (error) => {
          setError("email", {
            message: error.message,
          });
        },
        onCompleted: () => {
          setMode("display")();
        },
      });
    },
    [
      internalAdminEditUserMutationAPI,
      setError,
      setMode,
      user.id,
      user.organizationId,
      user.phone,
    ],
  );

  if (mode === "edit") {
    return (
      <form
        id="admin-edit-user-email-form"
        onSubmit={handleSubmit(onSubmit)}
        style={{ width: "100%" }}
      >
        <FormControl isInvalid={!!errors?.email} w="full">
          <Stack w="full" spacing={0}>
            <HStack w="full" spacing={1}>
              <InputGroup fontWeight={400} color={colors.gray50} fontSize={14}>
                <Input
                  id="email"
                  type="email"
                  size="sm"
                  borderRadius={4}
                  borderWidth={2}
                  pl={5}
                  borderColor={colors.blue}
                  {...register("email")}
                />
                <InputRightElement h="100%" w={7}>
                  <IconButton
                    aria-label="clear email"
                    icon={<CancelIcon boxSize={5} fill={colors.gray40} />}
                    variant="ghost"
                    size="xxs"
                    onClick={onClearClicked}
                  />
                </InputRightElement>
              </InputGroup>
              <IconButton
                aria-label="save"
                type="submit"
                icon={
                  loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <CheckIcon boxSize={4} fill={colors.white} />
                  )
                }
                bg={colors.blue}
                variant="hover"
                size="xs"
                onClick={setMode("edit")}
                isDisabled={loading || isDuplicateEmail}
              />
              <IconButton
                aria-label="cancel"
                icon={<CloseIcon boxSize={4} fill={colors.gray40} />}
                borderColor={colors.gray40}
                variant="outline"
                size="xs"
                onClick={setMode("display")}
                isDisabled={loading}
              />
            </HStack>
            <FormErrorMessage m={0}>
              {getMessageFromError(errors?.email?.message)}
            </FormErrorMessage>
          </Stack>
        </FormControl>
      </form>
    );
  }

  if (isEmpty(user.email)) {
    return (
      <Button
        variant="ghost"
        size="xxs"
        fontSize={12}
        fontWeight={500}
        color={colors.gray40}
        onClick={setMode("edit")}
      >
        + Add Email
      </Button>
    );
  }

  return (
    <HStack
      w="full"
      spacing={4}
      borderRadius={4}
      py="6px"
      px={1}
      _hover={{
        backgroundColor: colors.blue20,
      }}
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
    >
      <Text fontSize={14} fontWeight={500} color={colors.gray50} w="full">
        {user.email}
      </Text>
      <IconButton
        aria-label="Edit user name"
        icon={<PencilOutlineIcon boxSize={5} fill={colors.blue} />}
        variant="ghost"
        size="xxs"
        onClick={setMode("edit")}
        visibility={isHovering ? "visible" : "hidden"}
      />
    </HStack>
  );
};

export default memo(InternalAdminEditUserEmail);
